import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { CheckField, FormGrid, TextField } from '../../Components/Form';
import StatusBadge from '../../Components/Common/StatusBadge';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const IndexClauseList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [modal, setModal] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalIndexClause, setModalIndexClause] = useState(false);
  const [indexPriceFormData, setIndexClauseFormData] = useState({
    clauseTitle: null,
    ordreNumber: null,
    isShow: null,
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(
          `${api.API_URL}/clause-detail/${invoice.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Clause Detail deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token, search, statusFilter);
      } catch (err) {
        notify.apiError(err, 'delete clause detail');
      }
    }
  };

  const toggleIndexClauseClause = () => {
    setModal(!modal);
  };

  const columns = [
    {
      id: 'clauseTitle',
      name: 'Title',
      selector: row => row.clauseTitle,
      sortable: true,
      width: '60%',
      cell: row => (
        <span className='to-title' title={String(row.clauseTitle ?? '')}>
          {row.clauseTitle ?? '-'}
        </span>
      ),
    },

    {
      id: 'ordreNumber',
      name: 'Order No',
      selector: row => row.ordreNumber,
      sortable: true,
      width: '12%',
      cell: row => row.ordreNumber ?? '-',
    },

    {
      id: 'isShow',
      name: 'Active',
      selector: row => row.isShow,
      sortable: true,
      width: '13%',
      cell: row => (
        <StatusBadge
          tone={row.isShow ? 'success' : 'muted'}
          label={row.isShow ? 'Active' : 'Inactive'}
        />
      ),
    },

    {
      name: <span className='text-center w-100'>Action</span>,
      width: '15%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Clause Detail';

  const handleChange = e => {
    const { name, value } = e.target;
    setIndexClauseFormData({ ...indexPriceFormData, [name]: value });
  };

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search, statusFilter);
  }, [currentPage, rowsPerPage, token, search, statusFilter]);

  const handleStatusFilterChange = value => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setCurrentPage(1);
    setStatusFilter('');
  };

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
    statusFilter && {
      key: 'status',
      label: 'Status',
      value:
        STATUS_OPTIONS.find(o => o.value === statusFilter)?.label ??
        statusFilter,
      onRemove: () => handleStatusFilterChange(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;

  const fetchData = async (currentPage, rowsPerPage, token, search, status) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    fetch(
      `${api.API_URL}/clause-detail?PageNumber=${currentPage}&PageSize=${rowsPerPage}${search ? `&Search=${encodeURIComponent(search)}` : ''}${status ? `&IsShow=${status === 'active'}` : ''}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    )
      .then(data => data.json())
      .then(
        result => {
          if (reqId !== requestRef.current) return; // stale response
          setData({
            items: result.data.items || [],
          });
          setTotalRows(result.data.totalItems || 0);
        },
        _error => {
          // Handle error appropriately
        }
      )
      .catch(_error => {
        // Handle error silently or show appropriate message
      })
      .finally(() => {
        if (reqId === requestRef.current) setLoading(false);
      });
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setIndexClauseFormData({
      clauseTitle: null,
      ordreNumber: null,
      isShow: null,
      id: null,
    });
    indexPriceClause();
  };

  const handleUpdateModal = async (mid = null) => {
    await fetchIndexClauseDetail(mid);
    indexPriceClause();
  };

  const indexPriceClause = () => {
    setModalIndexClause(!modalIndexClause);
  };

  const selectedFieldsIndexClause = {
    clauseTitle: indexPriceFormData.clauseTitle,
    ordreNumber: indexPriceFormData.ordreNumber,
    isShow: indexPriceFormData.isShow,
  };

  const handleAddDataIndexClause = async () => {
    try {
      await axios.post(
        `${api.API_URL}/clause-detail`,
        selectedFieldsIndexClause,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success('Clause Detail added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search, statusFilter);
    }
  };

  const fetchIndexClauseDetail = async mid => {
    try {
      const response = await axios.get(`${api.API_URL}/clause-detail/${mid}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: `application/json`,
        },
      });
      const data = response?.data?.data ?? response?.data ?? response;
      setIndexClauseFormData({
        clauseTitle: data.clauseTitle || null,
        ordreNumber: data.ordreNumber || null,
        isShow: data.isShow ?? null,
        id: data.id || null,
      });
    } catch (error) {
      // Handle error silently or show appropriate message
    }
  };

  const handleUpdDataIndexClause = async mid => {
    try {
      await axios.put(
        `${api.API_URL}/clause-detail/${mid}`,
        selectedFieldsIndexClause,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search, statusFilter);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this clause detail?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.clauseTitle}
          confirmLabel='Delete clause detail'
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'Clause Detail',
                      to: '/apps-clause/view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-file-list-3-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Clause Detail</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the clause details and their display order.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(
                            currentPage,
                            rowsPerPage,
                            token,
                            search,
                            statusFilter
                          )
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Clause Detail
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search clause details...'
                      ariaLabel='Search clause details'
                    />
                    <ListFilterSelect
                      label='Status'
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      allLabel='All Status'
                      options={STATUS_OPTIONS}
                      ariaLabel='Filter by status'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} clause details`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='ordreNumber'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No clause detail found'
                          emptyHint='No clause details have been created yet. Clause details added here can be used in document templates.'
                          filteredHint='No clause detail matches the current filters.'
                          hasFilters={hasFilters}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Clause Detail',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalIndexClause}
            role='dialog'
            autoFocus={true}
            centered
            id='addIndexClauseClause'
            toggle={toggleIndexClauseClause}
            data-bs-backdrop='static'
            size='sm'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalIndexClause(!modalIndexClause);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {indexPriceFormData.id !== null
                  ? 'Edit Clause Detail'
                  : 'Add Clause Detail'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={1}>
                <TextField
                  label='Title'
                  name='clauseTitle'
                  placeholder='e.g. Payment terms'
                  value={indexPriceFormData.clauseTitle}
                  onChange={handleChange}
                />
                <TextField
                  label='Order number'
                  name='ordreNumber'
                  placeholder='e.g. 1'
                  value={indexPriceFormData.ordreNumber}
                  onChange={handleChange}
                />
                <CheckField
                  switchStyle
                  label='Active'
                  name='isShow'
                  checked={indexPriceFormData.isShow ?? false}
                  onChange={e =>
                    setIndexClauseFormData(prev => ({
                      ...prev,
                      isShow: e.target.checked,
                    }))
                  }
                />
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalIndexClause(!modalIndexClause);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={async () => {
                  await (indexPriceFormData.id !== null
                    ? handleUpdDataIndexClause(indexPriceFormData.id)
                    : handleAddDataIndexClause());
                  setModalIndexClause(false);
                }}
              >
                {indexPriceFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default IndexClauseList;
